import { Link, usePage } from "@inertiajs/react";
import { ChevronRight } from "lucide-react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import HelpSideNav from "@/components/help/HelpSideNav";
import { ROLES } from "@/help/roles";

/**
 * Chrome for every help-centre page.
 *
 *  - Uses the public marketing nav + footer so unauthenticated visitors
 *    can use the help centre, but authenticated users still see their
 *    "Dashboard" entry in the nav.
 *  - Inside <main>, renders a two-column layout:
 *      [ side-nav (sticky) ] [ content ]
 *    The side-nav is keyed off the supplied `roleSlug`. Pass `null`
 *    to render the chrome without a side-nav (e.g. the index page).
 *
 * Props:
 *   - roleSlug:  which role's curriculum to show in the side-nav
 *                (or null to hide the nav entirely)
 *   - activeArticleSlug:  highlight this article in the side-nav
 *   - breadcrumbs: [{label, href}] above the title, optional
 *   - title: page h1 (rendered inside content column)
 *   - eyebrow: small label above the title
 *   - children: page body
 */
export default function HelpLayout({
  roleSlug,
  activeArticleSlug,
  breadcrumbs,
  title,
  eyebrow,
  children,
}) {
  const role = roleSlug ? ROLES[roleSlug] : null;

  return (
    <MarketingLayout>
      <div className="container max-w-6xl py-10 md:py-14">
        <div className={role ? "grid gap-10 md:grid-cols-[16rem_1fr]" : ""}>
          {role && (
            // Sticky + own-scroll: as the article body scrolls past the
            // viewport, the side-nav stays in place; long curricula scroll
            // independently inside the aside instead of pushing the page.
            // top-20 (5rem) clears the marketing nav header (h-16 + a 1rem
            // gap), so max-height = 100vh - 5rem fits without clipping.
            <aside className="md:sticky md:top-20 md:self-start md:max-h-[calc(100vh-5rem)] md:overflow-y-auto md:pr-1 md:pb-6">
              <details className="rounded-lg border border-border bg-card/50 p-4 md:hidden">
                <summary className="cursor-pointer text-sm font-medium">Browse the {role.short.toLowerCase()} guide</summary>
                <div className="pt-4"><HelpSideNav role={role} activeArticleSlug={activeArticleSlug} /></div>
              </details>
              <div className="hidden md:block"><HelpSideNav role={role} activeArticleSlug={activeArticleSlug} /></div>
            </aside>
          )}

          <div className="min-w-0">
            {breadcrumbs && breadcrumbs.length > 0 && (
              <Breadcrumbs items={breadcrumbs} />
            )}
            {(title || eyebrow) && (
              <header className="mb-8">
                {eyebrow && (
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    {eyebrow}
                  </p>
                )}
                {title && (
                  <h1 className="font-display text-3xl md:text-4xl font-semibold text-foreground">
                    {title}
                  </h1>
                )}
              </header>
            )}
            {children}
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}

function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {items.map((it, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="h-3 w-3 opacity-50" />}
            {it.href && !isLast ? (
              <Link href={it.href} className="hover:text-primary transition-colors">
                {it.label}
              </Link>
            ) : (
              <span className={isLast ? "text-foreground font-medium" : ""}>{it.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
